import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ClickAwayListener, Popover } from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import { cn } from 'lib/utils';

/**
 * Reusable dropdown button: opens a popover with options; each option triggers an action on select.
 * Uses Tailwind for styling and MUI ClickAwayListener to close on outside click.
 *
 * @param {Array<{ id: string, label: string }>} options - List of options to show in the dropdown
 * @param {(id: string) => void} onSelect - Called when an option is clicked (receives option id)
 * @param {string} [activeId] - Id of the currently selected option (for highlight)
 * @param {string} [defaultId] - Id of the default option — if activeId equals this, filter is NOT considered active
 * @param {React.ReactNode} [buttonIcon] - Lucide icon to show in the button (color will be injected automatically)
 * @param {string} [buttonText] - Text to show in the button
 * @param {string} [buttonVariant] - Button variant (default, primary, etc.)
 * @param {string} [buttonSize] - Button size (small, medium, etc.)
 * @param {string} [className] - Extra class for the wrapper
 * @param {string} [placeholder] - Placeholder when no option is selected (e.g. "Filter")
 * @param {boolean} [iconOnly] - If true, button shows only the icon (no active option text) and uses variant for active state
 * @param {string} [activeIconColor] - Color applied to the icon when a non-default filter is active (default: CSS var --color-primary)
 */
function OptionsDropdownButton({
  options,
  onSelect,
  activeId,
  defaultId,
  buttonIcon,
  buttonText,
  buttonVariant = 'default',
  buttonSize = 'small',
  className,
  placeholder = 'Options',
  iconOnly = false
}) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = e => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleClickAway = () => {
    if (open) handleClose();
  };

  const handleOptionClick = id => {
    onSelect(id);
    handleClose();
  };

  const activeOption = options.find(o => o.id === activeId);
  const displayLabel = activeOption ? activeOption.label : placeholder;
  const showText = !iconOnly;
  const effectiveText = showText ? buttonText ?? displayLabel : undefined;

  // Filter is considered active only when activeId is set and differs from defaultId
  const isFilterActive = !!activeId && activeId !== defaultId;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={cn('relative inline-flex', className)}>
        <Button
          size={buttonSize}
          variant={isFilterActive ? 'primaryLight' : buttonVariant}
          onClick={handleOpen}
          icon={buttonIcon}
          text={effectiveText}
          title={iconOnly && activeOption ? activeOption.label : placeholder}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          style={{ marginTop: 6 }}
        >
          <ul
            className='list-none py-1 max-h-[280px] overflow-y-auto m-0 px-0'
            aria-label={placeholder}
          >
            {options.map(opt => (
              <li key={opt.id} role='none' className='list-none m-0 p-0'>
                <button
                  role='menuitem'
                  aria-current={opt.id === activeId ? 'true' : undefined}
                  type='button'
                  onClick={() => handleOptionClick(opt.id)}
                  className={cn(
                    'w-full text-left px-4 py-2.5 text-[length:--font-size-body] transition-colors border-none',
                    'hover:bg-primary-light hover:text-primary-darkened',
                    opt.id === activeId
                      ? 'bg-primary-light text-primary-darkened font-medium'
                      : 'text-black bg-white cursor-pointer'
                  )}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </Popover>
      </div>
    </ClickAwayListener>
  );
}

OptionsDropdownButton.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  activeId: PropTypes.string,
  defaultId: PropTypes.string,
  buttonIcon: PropTypes.node,
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.string,
  buttonSize: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  iconOnly: PropTypes.bool
};

export default OptionsDropdownButton;
